import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin } from "lucide-react";

const statusColor = (status) => {
  if (status === "pending_payment") return "#F59E0B";
  if (status === "completed_by_nurse" || status === "completed") return "#22C55E";
  return "#00E073";
};

// Numbered teardrop marker showing the intervention order
const numberedIcon = (n, color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#0D1C32;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.45);border:2px solid #fff;"><span style="transform:rotate(45deg);font-weight:700;font-size:13px;line-height:1;">${n}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });

const formatDuration = (minutes) => {
  const m = parseInt(minutes, 10);
  if (!m || isNaN(m)) return null;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h > 0 && min > 0) return `${h} h ${min}`;
  if (h > 0) return `${h} h`;
  return `${min} min`;
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
    } else if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [points, map]);
  return null;
};

const InterventionsMap = ({ interventions = [] }) => {
  const [points, setPoints] = useState([]);
  const [routeCoords, setRouteCoords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Only interventions with an address, sorted chronologically
  const withAddress = interventions
    .filter((i) => i.address)
    .sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const addressKey = withAddress
    .map((i) => `${i.address}|${i.postal_code}|${i.city}`)
    .join("~");

  useEffect(() => {
    let cancelled = false;
    const geocodeAll = async () => {
      setLoading(true);
      const results = [];
      for (let idx = 0; idx < withAddress.length; idx++) {
        const iv = withAddress[idx];
        const q = `${iv.address} ${iv.city || ""}`.trim();
        try {
          const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}${
            iv.postal_code ? `&postcode=${iv.postal_code}` : ""
          }&limit=1`;
          const res = await fetch(url);
          const data = await res.json();
          const feat = data?.features?.[0];
          if (feat?.geometry?.coordinates) {
            const [lng, lat] = feat.geometry.coordinates;
            results.push({ ...iv, lat, lng, order: idx + 1 });
          }
        } catch (e) {
          console.debug("Geocoding failed for", q, e);
        }
      }
      if (!cancelled) {
        setPoints(results);
        setLoading(false);
        // Fetch a real driving route through the stops (OSRM public API), fallback to straight lines
        if (results.length >= 2) {
          const coordsParam = results.map((p) => `${p.lng},${p.lat}`).join(";");
          try {
            const routeRes = await fetch(
              `https://router.project-osrm.org/route/v1/driving/${coordsParam}?overview=full&geometries=geojson`
            );
            const routeData = await routeRes.json();
            const geo = routeData?.routes?.[0]?.geometry?.coordinates;
            if (!cancelled && geo) {
              setRouteCoords(geo.map(([lng, lat]) => [lat, lng]));
            } else if (!cancelled) {
              setRouteCoords(results.map((p) => [p.lat, p.lng]));
            }
          } catch (e) {
            if (!cancelled) setRouteCoords(results.map((p) => [p.lat, p.lng]));
          }
        } else if (!cancelled) {
          setRouteCoords([]);
        }
      }
    };
    if (withAddress.length > 0) {
      geocodeAll();
    } else {
      setPoints([]);
      setLoading(false);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressKey]);

  if (withAddress.length === 0) return null;

  if (loading) {
    return (
      <div className="h-64 rounded-xl bg-[#1F2A38] border border-[#2A3A4D] flex items-center justify-center" data-testid="interventions-map-loading">
        <Loader2 className="w-6 h-6 text-[#00E073] animate-spin" />
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="h-24 rounded-xl bg-[#1F2A38] border border-[#2A3A4D] flex items-center justify-center gap-2 text-sm text-[#94A3B8]">
        <MapPin className="w-4 h-4" /> Adresses non localisables sur la carte
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden border border-[#2A3A4D]" data-testid="interventions-map">
      <MapContainer
        center={[points[0].lat, points[0].lng]}
        zoom={13}
        style={{ height: "320px", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {routeCoords.length >= 2 && (
          <Polyline positions={routeCoords} pathOptions={{ color: "#00E073", weight: 4, opacity: 0.75 }} />
        )}
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={numberedIcon(p.order, statusColor(p.status))}>
            <Popup>
              <div className="text-sm">
                <p className="font-semibold">
                  {p.order}. {p.time}
                  {formatDuration(p.duration_minutes) && ` · ${formatDuration(p.duration_minutes)}`}
                </p>
                {p.patient_name && <p>{p.patient_name}</p>}
                <p className="text-gray-600">{p.address}</p>
                <p className="text-gray-600">{p.postal_code} {p.city}</p>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default InterventionsMap;
